import { defineStore } from 'pinia'
import { computed, ref, watch } from 'vue'

import { extractError } from '@/api/client'
import { ventasApi } from '@/api/ventas'
import { useVentasStore } from '@/stores/ventas'
import type { VentaInput, VentaLoteInput } from '@/types/api'
import { esErrorDeRed } from '@/utils/red'

export interface VentaPendiente {
  /** Clave de idempotencia: el servidor no la registra dos veces. */
  clave: string
  payload: VentaInput
  /** Si viene, es un ticket con varias líneas; `payload` se ignora al enviar. */
  lote?: VentaLoteInput
  producto_nombre: string
  total: number
  /** Hora en que se hizo la venta en el dispositivo (ISO). */
  creado: string
  intentos: number
  /** Error del servidor (no de red): la venta necesita revisión humana. */
  error: string | null
}

const KEY = 'cv_cola_ventas_v1'
const INTERVALO_MS = 30_000

function leer(): VentaPendiente[] {
  try {
    const raw = localStorage.getItem(KEY)
    const data = raw ? (JSON.parse(raw) as unknown) : []
    return Array.isArray(data) ? (data as VentaPendiente[]) : []
  } catch {
    return []
  }
}

/**
 * Cola de ventas hechas sin conexión. Se guarda en el dispositivo y se envía
 * al servidor cuando vuelve la red. Cada venta lleva su clave de idempotencia
 * y su hora real, así que reintentar es seguro y el histórico queda correcto.
 *
 * Todo lo que sale de la cola viaja con `offline: true`: con la facturación
 * electrónica encendida, el servidor crea su documento en contingencia (venta
 * sin conexión, 48 horas para transmitirlo a la DIAN) con la hora real.
 */
export const useColaStore = defineStore('cola', () => {
  const items = ref<VentaPendiente[]>(leer())
  const sincronizando = ref(false)
  let timer: number | null = null
  let iniciado = false

  const pendientes = computed(() => items.value.length)
  const conError = computed(() => items.value.filter((i) => i.error).length)
  const totalPendiente = computed(() => items.value.reduce((acc, i) => acc + i.total, 0))

  watch(
    items,
    (v) => {
      try {
        localStorage.setItem(KEY, JSON.stringify(v))
      } catch {
        /* almacenamiento lleno o bloqueado: la cola sigue en memoria */
      }
    },
    { deep: true },
  )

  function encolar(payload: VentaInput, meta: { producto_nombre: string; total: number }) {
    const clave = payload.clave_idempotencia ?? `pos-${Date.now()}-${Math.random().toString(36).slice(2)}`
    items.value = [
      ...items.value,
      {
        clave,
        payload: { ...payload, clave_idempotencia: clave },
        producto_nombre: meta.producto_nombre,
        total: meta.total,
        creado: payload.fecha_hora_cliente ?? new Date().toISOString(),
        intentos: 0,
        error: null,
      },
    ]
    programar()
  }

  function encolarLote(lote: VentaLoteInput, meta: { producto_nombre: string; total: number }) {
    const clave = lote.clave_idempotencia ?? `tk-${Date.now()}-${Math.random().toString(36).slice(2)}`
    const primero = lote.items[0]
    items.value = [
      ...items.value,
      {
        clave,
        payload: { producto_id: primero?.producto_id ?? 0, cantidad: primero?.cantidad ?? 1 },
        lote: { ...lote, clave_idempotencia: clave },
        producto_nombre: meta.producto_nombre,
        total: meta.total,
        creado: lote.fecha_hora_cliente ?? new Date().toISOString(),
        intentos: 0,
        error: null,
      },
    ]
    programar()
  }

  function quitar(clave: string) {
    items.value = items.value.filter((i) => i.clave !== clave)
  }

  /** Envía en orden. Se detiene al primer error de red; los errores del servidor se anotan. */
  async function sincronizar(): Promise<{ enviadas: number; fallidas: number }> {
    if (sincronizando.value || !items.value.length) return { enviadas: 0, fallidas: 0 }
    if (typeof navigator !== 'undefined' && !navigator.onLine) return { enviadas: 0, fallidas: 0 }
    sincronizando.value = true
    const ventasStore = useVentasStore()
    let enviadas = 0
    let fallidas = 0
    try {
      for (const item of [...items.value]) {
        try {
          if (item.lote) {
            const r = await ventasApi.registrarLote({
              ...item.lote,
              clave_idempotencia: item.clave,
              fecha_hora_cliente: item.creado,
              offline: true,
            })
            quitar(item.clave)
            for (const v of r.ventas) ventasStore.agregar(v)
          } else {
            const venta = await ventasApi.registrar({
              ...item.payload,
              clave_idempotencia: item.clave,
              fecha_hora_cliente: item.creado,
              offline: true,
            })
            quitar(item.clave)
            ventasStore.agregar(venta)
          }
          enviadas += 1
        } catch (e) {
          if (esErrorDeRed(e)) break // sigue sin red: lo intentamos después
          fallidas += 1
          items.value = items.value.map((i) =>
            i.clave === item.clave
              ? { ...i, intentos: i.intentos + 1, error: extractError(e, 'El servidor rechazó la venta') }
              : i,
          )
        }
      }
    } finally {
      sincronizando.value = false
    }
    return { enviadas, fallidas }
  }

  function programar() {
    if (timer !== null || typeof window === 'undefined') return
    timer = window.setInterval(() => {
      if (!items.value.length) {
        if (timer !== null) window.clearInterval(timer)
        timer = null
        return
      }
      sincronizar()
    }, INTERVALO_MS)
  }

  /** Engancha reconexión y arranca el reintento periódico si hay cola. */
  function iniciar() {
    if (iniciado || typeof window === 'undefined') return
    iniciado = true
    window.addEventListener('online', () => {
      sincronizar()
    })
    if (items.value.length) {
      sincronizar()
      programar()
    }
  }

  return { items, pendientes, conError, totalPendiente, sincronizando, encolar, encolarLote, quitar, sincronizar, iniciar }
})
